/**
 * Extension registries for drop-in addons.
 *
 * Two registries are exposed via window.Veyop (see veyopRuntime.ts):
 *   - tools: per-popover button registry (text tools today; image tools later)
 *   - itemRenderers: custom render fn for design items matching a predicate
 *   - editPanels: plugin-owned left inspector sub-views
 *   - itemBounds: plugin-owned natural bounds measurement for design items
 *   - textStyleSupport: which font styles (bold/italic) an item's renderer can really draw
 *   - adminPages: plugin-owned wp-admin SPA pages
 *
 * Both registries are reactive (shallowReactive) so Vue components can
 * subscribe via the useTextTools() / useItemRenderers() composables.
 */
import { shallowReactive, computed, type ComputedRef, type Component } from 'vue';
import type { AdminPageIcon } from '@shared/adminIcon';
import type { DesignerIconInput } from '@shared/designerIcon';
import { t } from '@shared/i18n';
import type { DesignItem } from './types';

export type { AdminMaterialIcon, AdminPageIcon } from '@shared/adminIcon';
export type { DesignerIconInput, DesignerIconSpec, DesignerIconType } from '@shared/designerIcon';

export interface VeyopTextToolContext {
  item: DesignItem;
  /** Apply a partial patch to the selected item (delegates to design.updateItem). */
  update: (patch: Record<string, unknown>) => void;
}

export type VeyopTextToolRender =
  | { component: Component; props?: Record<string, unknown> }
  | HTMLElement;

export interface VeyopTextTool {
  /** Stable id; re-registering with the same id replaces the existing entry. */
  id: string;
  /** Tooltip / accessible label. */
  label: string;
  /** Optional inline SVG markup or icon name; the host decides how to render it. */
  icon?: string;
  /** Returns either a Vue component+props (preferred) or a raw HTMLElement to mount. */
  render: (ctx: VeyopTextToolContext) => VeyopTextToolRender;
  /** Lower numbers render earlier in the toolbar (default 100). */
  priority?: number;
}

export interface VeyopImageToolContext {
  item: DesignItem;
  /** Apply a partial patch to the selected image item. */
  update: (patch: Record<string, unknown>) => void;
}

export type VeyopImageToolRender =
  | { component: Component; props?: Record<string, unknown> }
  | HTMLElement;

export interface VeyopImageTool {
  /** Stable id; re-registering with the same id replaces the existing entry. */
  id: string;
  /** Tooltip / accessible label. */
  label: string;
  /** Optional inline SVG markup or icon name; the host decides how to render it. */
  icon?: string;
  /** Returns either a Vue component+props (preferred) or a raw HTMLElement to mount. */
  render: (ctx: VeyopImageToolContext) => VeyopImageToolRender;
  /** Lower numbers render earlier in the toolbar (default 100). */
  priority?: number;
}

export interface VeyopItemRenderer {
  /** Stable id; re-registering with the same id replaces the existing entry. */
  id: string;
  /** Predicate; first matching renderer wins (lowest priority first). */
  matches: (item: DesignItem) => boolean;
  /**
   * Mount the custom rendering inside `container`. Return a cleanup fn that the
   * host calls when the renderer no longer matches or the item unmounts.
   */
  render: (item: DesignItem, container: HTMLElement) => () => void;
  /** Lower numbers checked first (default 100). */
  priority?: number;
}

export interface VeyopEditPanelContext {
  item: DesignItem | null;
  /** Apply a partial patch to the selected item. No-op when nothing is selected. */
  update: (patch: Record<string, unknown>) => void;
}

export type VeyopEditPanelRender =
  | { component: Component; props?: Record<string, unknown> }
  | HTMLElement;

export interface VeyopEditPanel {
  /** Stable id used with ui.setEditSubView(id). */
  id: string;
  /** Human-readable panel label. */
  label: string;
  /** Optional host placement for listing plugin panels in built-in views. */
  placement?: 'text-effects';
  /** Short description shown in host lists. */
  description?: string;
  /** IDs hidden while this panel itself matches the selected item. */
  replaces?: string[];
  /** Optional inline SVG markup or icon name; the host decides how to render it. */
  icon?: string;
  /** Optional inline SVG thumbnail illustrating the effect (shown on the Effects card). */
  preview?: string;
  /** True when the effect is applied to this item; the Effects card shows an "On" badge. */
  isActive?: (item: DesignItem | null) => boolean;
  /** Optional visibility predicate. Host hides panel when it returns false. */
  matches?: (item: DesignItem | null, context: VeyopEditPanelContext) => boolean;
  /** Returns either a Vue component+props (preferred) or a raw HTMLElement to mount. */
  render: (ctx: VeyopEditPanelContext) => VeyopEditPanelRender;
  /** Lower numbers render earlier when listing panels (default 100). */
  priority?: number;
}

export interface VeyopItemBounds {
  width: number;
  height: number;
}

export interface VeyopItemBoundsResolver {
  /** Stable id; re-registering with the same id replaces the existing entry. */
  id: string;
  /** Predicate; first matching resolver wins (lowest priority first). */
  matches: (item: DesignItem) => boolean;
  /** Returns the natural unrotated item bounds in canvas pixels. */
  measure: (item: DesignItem) => VeyopItemBounds | null;
  /**
   * Optional: record a box the user dragged to, as a patch on the item.
   *
   * A resolver owns its item's geometry, so the host cannot just write `width`/`height` —
   * the next `measure()` would recompute them from the addon's own state and undo the drag.
   * Resolvers that implement this get the dragged box and return whatever they need to
   * persist (e.g. a visual scale factor); the host applies the patch and skips its own refit.
   */
  fitToBox?: (item: DesignItem, box: VeyopItemBounds) => Record<string, unknown> | null;
  /**
   * The item redraws itself for any box (e.g. generated shapes), so the host may offer
   * one-axis side handles instead of corner-only uniform scaling. Needs `fitToBox`.
   */
  freeAspect?: boolean;
  /** Lower numbers checked first (default 100). */
  priority?: number;
}

/** `false` = the face does not exist; `null`/absent = unknown or still probing (keep enabled). */
export interface VeyopTextStyleSupport {
  bold?: boolean | null;
  italic?: boolean | null;
}

export interface VeyopTextStyleSupportProvider {
  /** Stable id; re-registering with the same id replaces the existing entry. */
  id: string;
  /** Predicate; first matching provider wins (lowest priority first). */
  matches: (item: DesignItem) => boolean;
  /**
   * Which styles the renderer can draw for this item. Renderers that outline real font files
   * (instead of letting the browser synthesize) cannot invent a bold face a font does not ship,
   * so the host disables the matching toolbar button, the way Canva does.
   */
  supports: (item: DesignItem) => VeyopTextStyleSupport | null;
  /** Lower numbers checked first (default 100). */
  priority?: number;
}

export interface VeyopAdminPage {
  /** Stable id; re-registering with the same id replaces the existing entry. */
  id: string;
  /** Sidebar label and page title. */
  label: string;
  /** Hash route path, e.g. "/pricing". */
  path: string;
  /** Optional alternate hash route paths that resolve to the same page. */
  aliases?: string[];
  /** Legacy `<path d="..."/>` markup or `{ type: 'material', path }` for the admin sidebar. */
  icon?: AdminPageIcon;
  /** Vue component mounted by the admin addon page host. */
  component: Component;
  /** Lower numbers render earlier in the sidebar (default 100). */
  priority?: number;
  /**
   * Sidebar hub the page is listed under. Pages without a group (and not known to the core
   * map in adminNavHubs.ts) are listed under "Add-ons".
   */
  group?: VeyopAdminNavGroup;
  /** Shorter label used inside the hub's submenu; `label` stays the page title. */
  navLabel?: string;
}

export type VeyopAdminNavGroup = 'catalog' | 'production' | 'library' | 'designer' | 'pricing' | 'settings';

export interface VeyopAdminPageTab {
  id: string;
  pageId: string;
  label: string;
  component: Component;
  priority?: number;
}

export type VeyopAdminWidgetPlacement = 'dashboard' | 'apps';

export interface VeyopAdminWidget {
  id: string;
  placements: VeyopAdminWidgetPlacement[];
  component: Component;
  priority?: number;
  /** Home column: `main` (wide, default) or `side` (narrow cards next to it). */
  column?: 'main' | 'side';
}

export interface VeyopWooProductSection {
  id: string;
  label: string;
  component: Component;
  priority?: number;
}

export interface VeyopPrintingMethodProvider {
  /** Stable machine id stored in printing.values.method_pack. */
  id: string;
  label: string;
  description?: string;
  /** Old printing codes recognized when method_pack is absent. */
  legacyCodes?: string[];
  priority?: number;
}

export interface VeyopCatalogPrintingType {
  id?: number;
  title: string;
  printing_code: string;
  values?: Record<string, unknown>;
}

export interface VeyopCatalogEditContext {
  productId: number;
  activeView: string;
  /** Printing methods selected for this catalog product. */
  allowedPrintingCodes: string[];
  availablePrintings: VeyopCatalogPrintingType[];
  canvasWidth: number;
  canvasHeight: number;
  enabledViews: string[];
  printGeometry: Record<string, {
    physicalWidthCm: number;
    physicalHeightCm: number;
    shape: string;
  }>;
  updatePrintGeometry: (view: string, patch: { physicalWidthCm?: number; physicalHeightCm?: number; shape?: string }) => void;
  design: Record<string, unknown>;
  updateDesign: (key: string, value: unknown) => void;
  markDirty?: () => void;
}

export type VeyopCatalogEditRender =
  | { component: Component; props?: Record<string, unknown> }
  | HTMLElement;

export interface VeyopCatalogEditPanel {
  id: string;
  label: string;
  icon?: string;
  /** Catalog editor section the panel lives in. Per-side tools default to 'sides'. */
  section?: 'sides' | 'printing' | 'options';
  render: (ctx: VeyopCatalogEditContext) => VeyopCatalogEditRender;
  priority?: number;
}

/** A pill shown next to a product in the admin Catalog list (e.g. "Configurator"). */
export interface VeyopCatalogListBadge {
  label: string;
  title?: string;
  /** Admin hash route opened on click, e.g. '#/smart-configurator?product=3'. */
  href?: string;
  tone?: 'neutral' | 'success';
}

/** Supplies Catalog-list badges for many products in one call (keyed by catalog product id). */
export interface VeyopCatalogListBadgeProvider {
  id: string;
  load: () => Promise<Record<number, VeyopCatalogListBadge>>;
  priority?: number;
}

export interface VeyopCatalogCanvasOverlay {
  id: string;
  render: (ctx: VeyopCatalogEditContext) => VeyopCatalogEditRender;
  priority?: number;
}

export interface VeyopDesignerOverlayContext {
  viewName: string;
  config: Record<string, unknown>;
}

export type VeyopDesignerOverlayRender =
  | { component: Component; props?: Record<string, unknown> }
  | HTMLElement;

export interface VeyopDesignerOverlay {
  id: string;
  render: (ctx: VeyopDesignerOverlayContext) => VeyopDesignerOverlayRender;
  priority?: number;
}

export interface VeyopStatusPillContext {
  mobile: boolean;
}

export type VeyopStatusPillRender =
  | { component: Component; props?: Record<string, unknown> }
  | HTMLElement;

/** A bottom-bar status indicator (e.g. print-readiness pill) an addon can contribute. */
export interface VeyopStatusPill {
  id: string;
  render: (ctx: VeyopStatusPillContext) => VeyopStatusPillRender;
  priority?: number;
}

export interface VeyopDesignerLeftPanelContext {
  config: Record<string, unknown>;
  activeView: string;
}

export type VeyopDesignerLeftPanelRender =
  | { component: Component; props?: Record<string, unknown> }
  | HTMLElement;

export interface VeyopDesignerLeftPanel {
  id: string;
  label: string;
  /** Short shopper-facing description, including in the mobile More list. */
  description?: string;
  /** Stroke path string, or explicit stroke/material icon spec. */
  icon?: DesignerIconInput;
  render: (ctx: VeyopDesignerLeftPanelContext) => VeyopDesignerLeftPanelRender;
  priority?: number;
  /**
   * Shopper-facing group the panel lives in. Grouped panels show as a tab inside
   * that group (e.g. "Images › Free photos") instead of their own rail button.
   * Omit to keep a standalone rail button.
   */
  group?: VeyopDesignerPanelGroup;
  /** Short tab label inside its group (defaults to `label`). */
  tabLabel?: string;
}

export type VeyopDesignerPanelGroup = 'product' | 'text' | 'images' | 'graphics' | 'templates';

export interface VeyopDesignerLayout {
  /** Stable id; re-registering with the same id replaces the previous layout. */
  id: string;
  /** Optional theme slug this layout belongs to. */
  theme?: string;
  /** Vue component rendered by the designer layout host. Receives a `context` prop. */
  component: Component;
  /** Lower numbers win when more than one layout matches the active theme. */
  priority?: number;
}

export type VeyopUploadNoticeType = 'info' | 'success' | 'warning';

export interface VeyopUploadProcessorResult {
  /** File passed into the existing upload/downscale pipeline. */
  file: File;
  /** Optional notice shown only after the transformed file uploads successfully. */
  notice?: {
    type?: VeyopUploadNoticeType;
    message: string;
  };
  /** Processor-owned metadata available to the host and future integrations. */
  metadata?: Record<string, unknown>;
  /**
   * The file is already sized for print and under `maxUploadBytes`: the host skips its own
   * downscale (which would otherwise shrink it to 2500 px). The size cap is still enforced.
   */
  optimized?: boolean;
}

export interface VeyopUploadProcessorContext {
  /** Store upload cap in bytes; the processor's output must fit under it. */
  maxUploadBytes: number;
}

/**
 * Throw from `process` to show `userMessage` (already translated by the addon) instead of the
 * processor's generic `errorMessage`. Any error with a string `userMessage` qualifies.
 */
export interface VeyopUploadProcessorError extends Error {
  userMessage: string;
  /** The shopper backed out (e.g. closed a picker): the host drops the upload without an error. */
  cancelled?: boolean;
}

export interface VeyopUploadProcessor {
  /** Stable id; re-registering with the same id replaces the previous processor. */
  id: string;
  /** File-picker accept tokens contributed by this processor (for example `.pdf`). */
  accept: string[];
  /** Safe message shown when processing rejects. */
  errorMessage?: string;
  /**
   * Largest source file (MB) the processor accepts, when its output is much smaller than its
   * input (a PDF with embedded photos). Defaults to the store upload cap.
   */
  maxInputMb?: number;
  /** Select whether this processor handles the chosen file. */
  matches: (file: File) => boolean;
  /** Transform the chosen file before the existing upload flow runs. */
  process: (file: File, context: VeyopUploadProcessorContext) => Promise<VeyopUploadProcessorResult>;
  /** Lower numbers are considered first (default 100). */
  priority?: number;
}

const textTools = shallowReactive(new Map<string, VeyopTextTool>());
const imageTools = shallowReactive(new Map<string, VeyopImageTool>());
const itemRenderers = shallowReactive(new Map<string, VeyopItemRenderer>());
const editPanels = shallowReactive(new Map<string, VeyopEditPanel>());
const itemBoundsResolvers = shallowReactive(new Map<string, VeyopItemBoundsResolver>());
const textStyleSupportProviders = shallowReactive(new Map<string, VeyopTextStyleSupportProvider>());
const adminPages = shallowReactive(new Map<string, VeyopAdminPage>());
const adminPageTabs = shallowReactive(new Map<string, VeyopAdminPageTab>());
const adminWidgets = shallowReactive(new Map<string, VeyopAdminWidget>());
const wooProductSections = shallowReactive(new Map<string, VeyopWooProductSection>());
const printingMethodProviders = shallowReactive(new Map<string, VeyopPrintingMethodProvider>());
const catalogEditPanels = shallowReactive(new Map<string, VeyopCatalogEditPanel>());
const catalogListBadgeProviders = shallowReactive(new Map<string, VeyopCatalogListBadgeProvider>());
const catalogCanvasOverlays = shallowReactive(new Map<string, VeyopCatalogCanvasOverlay>());
const designerOverlays = shallowReactive(new Map<string, VeyopDesignerOverlay>());
const statusPills = shallowReactive(new Map<string, VeyopStatusPill>());
const designerLeftPanels = shallowReactive(new Map<string, VeyopDesignerLeftPanel>());
const designerLayouts = shallowReactive(new Map<string, VeyopDesignerLayout>());
const uploadProcessors = shallowReactive(new Map<string, VeyopUploadProcessor>());

function bySortedPriority<T extends { priority?: number }>(map: Map<string, T>): T[] {
  return [...map.values()].sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));
}

/** Remove panels superseded by another currently matching panel. */
export function filterReplacedEditPanels(panels: VeyopEditPanel[]): VeyopEditPanel[] {
  const replaced = new Set(panels.flatMap((panel) => panel.replaces ?? []));
  return panels.filter((panel) => !replaced.has(panel.id));
}

export function registerTextTool(tool: VeyopTextTool): () => void {
  if (!tool || !tool.id) {
    return () => {};
  }
  textTools.set(tool.id, tool);
  return () => {
    if (textTools.get(tool.id) === tool) {
      textTools.delete(tool.id);
    }
  };
}

export function getTextTools(): VeyopTextTool[] {
  return bySortedPriority(textTools);
}

export function registerImageTool(tool: VeyopImageTool): () => void {
  if (!tool || !tool.id) {
    return () => {};
  }
  imageTools.set(tool.id, tool);
  return () => {
    if (imageTools.get(tool.id) === tool) {
      imageTools.delete(tool.id);
    }
  };
}

export function getImageTools(): VeyopImageTool[] {
  return bySortedPriority(imageTools);
}

export function registerItemRenderer(renderer: VeyopItemRenderer): () => void {
  if (!renderer || !renderer.id) {
    return () => {};
  }
  itemRenderers.set(renderer.id, renderer);
  return () => {
    if (itemRenderers.get(renderer.id) === renderer) {
      itemRenderers.delete(renderer.id);
    }
  };
}

export function findItemRenderer(item: DesignItem): VeyopItemRenderer | null {
  for (const r of bySortedPriority(itemRenderers)) {
    try {
      if (r.matches(item)) {
        return r;
      }
    } catch {
      // ignore broken predicate, treat as no-match
    }
  }
  return null;
}

export function registerEditPanel(panel: VeyopEditPanel): () => void {
  if (!panel || !panel.id) {
    return () => {};
  }
  editPanels.set(panel.id, panel);
  return () => {
    if (editPanels.get(panel.id) === panel) {
      editPanels.delete(panel.id);
    }
  };
}

export function getEditPanels(): VeyopEditPanel[] {
  return bySortedPriority(editPanels);
}

export function findEditPanel(id: string): VeyopEditPanel | null {
  return editPanels.get(id) ?? null;
}

/** Vue composable returning a reactive, sorted list of registered text tools. */
export function useTextTools(): ComputedRef<VeyopTextTool[]> {
  return computed(() => bySortedPriority(textTools));
}

/** Vue composable returning a reactive, sorted list of registered image tools. */
export function useImageTools(): ComputedRef<VeyopImageTool[]> {
  return computed(() => bySortedPriority(imageTools));
}

/** Vue composable returning a reactive lookup for item renderers. */
export function useItemRenderers(): ComputedRef<VeyopItemRenderer[]> {
  return computed(() => bySortedPriority(itemRenderers));
}

/** Vue composable returning a reactive, sorted list of registered edit panels. */
export function useEditPanels(): ComputedRef<VeyopEditPanel[]> {
  return computed(() => bySortedPriority(editPanels));
}

export function registerItemBoundsResolver(resolver: VeyopItemBoundsResolver): () => void {
  if (!resolver || !resolver.id) {
    return () => {};
  }
  itemBoundsResolvers.set(resolver.id, resolver);
  return () => {
    if (itemBoundsResolvers.get(resolver.id) === resolver) {
      itemBoundsResolvers.delete(resolver.id);
    }
  };
}

export function getItemBoundsResolvers(): VeyopItemBoundsResolver[] {
  return bySortedPriority(itemBoundsResolvers);
}

/** The resolver that owns this item's geometry, or null when the host measures it itself. */
export function findItemBoundsResolver(item: DesignItem): VeyopItemBoundsResolver | null {
  for (const resolver of bySortedPriority(itemBoundsResolvers)) {
    try {
      if (resolver.matches(item)) return resolver;
    } catch {
      // ignore broken resolver, treat as no-match
    }
  }
  return null;
}

/** True when the owning resolver redraws the item for any aspect ratio (see `freeAspect`). */
export function itemSupportsFreeAspect(item: DesignItem): boolean {
  const resolver = findItemBoundsResolver(item);
  return Boolean(resolver?.freeAspect && resolver.fitToBox);
}

export function resolveItemBounds(item: DesignItem): VeyopItemBounds | null {
  for (const resolver of bySortedPriority(itemBoundsResolvers)) {
    try {
      if (resolver.matches(item)) {
        return resolver.measure(item);
      }
    } catch {
      // ignore broken resolver, treat as no-match
    }
  }
  return null;
}

/** Ask the owning resolver to record a dragged box; null when it has no opinion. */
export function fitItemToBox(item: DesignItem, box: VeyopItemBounds): Record<string, unknown> | null {
  const resolver = findItemBoundsResolver(item);
  if (!resolver?.fitToBox) return null;
  try {
    return resolver.fitToBox(item, box);
  } catch {
    return null;
  }
}

export function registerTextStyleSupport(provider: VeyopTextStyleSupportProvider): () => void {
  if (!provider || !provider.id || typeof provider.supports !== 'function') {
    return () => {};
  }
  textStyleSupportProviders.set(provider.id, provider);
  return () => {
    if (textStyleSupportProviders.get(provider.id) === provider) {
      textStyleSupportProviders.delete(provider.id);
    }
  };
}

/** Style support for an item; null when no provider owns it (the browser synthesizes styles). */
export function resolveTextStyleSupport(item: DesignItem | null | undefined): VeyopTextStyleSupport | null {
  if (!item) return null;
  for (const provider of bySortedPriority(textStyleSupportProviders)) {
    try {
      if (provider.matches(item)) return provider.supports(item);
    } catch {
      // ignore broken provider, treat as no-match
    }
  }
  return null;
}

export function registerAdminPage(page: VeyopAdminPage): () => void {
  if (!page || !page.id || !page.path || !page.component) {
    return () => {};
  }
  const normalized = {
    ...page,
    path: page.path.startsWith('/') ? page.path : `/${page.path}`,
    aliases: (page.aliases ?? []).map((alias) => alias.startsWith('/') ? alias : `/${alias}`),
  };
  adminPages.set(normalized.id, normalized);
  return () => {
    if (adminPages.get(normalized.id) === normalized) {
      adminPages.delete(normalized.id);
    }
  };
}

export function getAdminPages(): VeyopAdminPage[] {
  return bySortedPriority(adminPages);
}

export function findAdminPage(path: string): VeyopAdminPage | null {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const matchesPath = (candidate: string): boolean => {
    const routePath = candidate.startsWith('/') ? candidate : `/${candidate}`;
    return normalized === routePath || normalized.startsWith(`${routePath}/`);
  };
  return getAdminPages().find((page) =>
    matchesPath(page.path) || (page.aliases ?? []).some(matchesPath)
  ) ?? null;
}

export function useAdminPages(): ComputedRef<VeyopAdminPage[]> {
  return computed(() => bySortedPriority(adminPages));
}

export function registerAdminWidget(widget: VeyopAdminWidget): () => void {
  const placements = widget?.placements?.filter((placement) => placement === 'dashboard' || placement === 'apps') ?? [];
  if (!widget?.id || !widget.component || placements.length === 0) return () => {};
  const normalized = { ...widget, placements: [...new Set(placements)] };
  adminWidgets.set(widget.id, normalized);
  notifyAdminExtensionsChanged();
  return () => {
    if (adminWidgets.get(widget.id) === normalized) {
      adminWidgets.delete(widget.id);
      notifyAdminExtensionsChanged();
    }
  };
}

export function getAdminWidgets(placement?: VeyopAdminWidgetPlacement): VeyopAdminWidget[] {
  const widgets = bySortedPriority(adminWidgets);
  return placement ? widgets.filter((widget) => widget.placements.includes(placement)) : widgets;
}

export function useAdminWidgets(placement: VeyopAdminWidgetPlacement): ComputedRef<VeyopAdminWidget[]> {
  return computed(() => getAdminWidgets(placement));
}

function notifyAdminExtensionsChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('veyop:admin-extensions-changed'));
  }
}

export function registerAdminPageTab(tab: VeyopAdminPageTab): () => void {
  if (!tab?.id || !tab.pageId || !tab.label || !tab.component) return () => {};
  const key = `${tab.pageId}:${tab.id}`;
  adminPageTabs.set(key, tab);
  notifyAdminExtensionsChanged();
  return () => {
    if (adminPageTabs.get(key) === tab) {
      adminPageTabs.delete(key);
      notifyAdminExtensionsChanged();
    }
  };
}

export function getAdminPageTabs(pageId: string): VeyopAdminPageTab[] {
  return bySortedPriority(new Map([...adminPageTabs].filter(([, tab]) => tab.pageId === pageId)));
}

export function registerWooProductSection(section: VeyopWooProductSection): () => void {
  if (!section?.id || !section.label || !section.component) return () => {};
  wooProductSections.set(section.id, section);
  notifyAdminExtensionsChanged();
  return () => {
    if (wooProductSections.get(section.id) === section) {
      wooProductSections.delete(section.id);
      notifyAdminExtensionsChanged();
    }
  };
}

export function getWooProductSections(): VeyopWooProductSection[] {
  return bySortedPriority(wooProductSections);
}

export function registerPrintingMethodProvider(provider: VeyopPrintingMethodProvider): () => void {
  if (!provider?.id || !provider.label) return () => {};
  printingMethodProviders.set(provider.id, provider);
  notifyAdminExtensionsChanged();
  return () => {
    if (printingMethodProviders.get(provider.id) === provider) {
      printingMethodProviders.delete(provider.id);
      notifyAdminExtensionsChanged();
    }
  };
}

export function getPrintingMethodProviders(): VeyopPrintingMethodProvider[] {
  return bySortedPriority(printingMethodProviders);
}

export function resolvePrintingMethodProviderId(printing?: VeyopCatalogPrintingType | null): string {
  if (!printing) return '';
  const explicit = String(printing.values?.method_pack ?? '').trim();
  if (explicit) return explicit;
  const code = String(printing.printing_code ?? '').trim();
  return getPrintingMethodProviders().find((provider) =>
    (provider.legacyCodes ?? []).includes(code)
  )?.id ?? '';
}

export function registerCatalogEditPanel(panel: VeyopCatalogEditPanel): () => void {
  if (!panel || !panel.id || !panel.render) {
    return () => {};
  }
  catalogEditPanels.set(panel.id, panel);
  return () => {
    if (catalogEditPanels.get(panel.id) === panel) {
      catalogEditPanels.delete(panel.id);
    }
  };
}

export function getCatalogEditPanels(): VeyopCatalogEditPanel[] {
  return bySortedPriority(catalogEditPanels);
}

export function useCatalogEditPanels(): ComputedRef<VeyopCatalogEditPanel[]> {
  return computed(() => bySortedPriority(catalogEditPanels));
}

export function registerCatalogListBadges(provider: VeyopCatalogListBadgeProvider): () => void {
  if (!provider?.id || typeof provider.load !== 'function') return () => {};
  catalogListBadgeProviders.set(provider.id, provider);
  return () => {
    if (catalogListBadgeProviders.get(provider.id) === provider) catalogListBadgeProviders.delete(provider.id);
  };
}

export function useCatalogListBadgeProviders(): ComputedRef<VeyopCatalogListBadgeProvider[]> {
  return computed(() => bySortedPriority(catalogListBadgeProviders));
}

export function registerCatalogCanvasOverlay(overlay: VeyopCatalogCanvasOverlay): () => void {
  if (!overlay || !overlay.id || !overlay.render) {
    return () => {};
  }
  catalogCanvasOverlays.set(overlay.id, overlay);
  return () => {
    if (catalogCanvasOverlays.get(overlay.id) === overlay) {
      catalogCanvasOverlays.delete(overlay.id);
    }
  };
}

export function getCatalogCanvasOverlays(): VeyopCatalogCanvasOverlay[] {
  return bySortedPriority(catalogCanvasOverlays);
}

export function useCatalogCanvasOverlays(): ComputedRef<VeyopCatalogCanvasOverlay[]> {
  return computed(() => bySortedPriority(catalogCanvasOverlays));
}

export function registerDesignerOverlay(overlay: VeyopDesignerOverlay): () => void {
  if (!overlay || !overlay.id || !overlay.render) {
    return () => {};
  }
  designerOverlays.set(overlay.id, overlay);
  return () => {
    if (designerOverlays.get(overlay.id) === overlay) {
      designerOverlays.delete(overlay.id);
    }
  };
}

export function getDesignerOverlays(): VeyopDesignerOverlay[] {
  return bySortedPriority(designerOverlays);
}

export function useDesignerOverlays(): ComputedRef<VeyopDesignerOverlay[]> {
  return computed(() => bySortedPriority(designerOverlays));
}

export function registerStatusPill(pill: VeyopStatusPill): () => void {
  if (!pill || !pill.id || !pill.render) {
    return () => {};
  }
  statusPills.set(pill.id, pill);
  return () => {
    if (statusPills.get(pill.id) === pill) {
      statusPills.delete(pill.id);
    }
  };
}

export function getStatusPills(): VeyopStatusPill[] {
  return bySortedPriority(statusPills);
}

export function useStatusPills(): ComputedRef<VeyopStatusPill[]> {
  return computed(() => bySortedPriority(statusPills));
}

export function registerDesignerLeftPanel(panel: VeyopDesignerLeftPanel): () => void {
  if (!panel || !panel.id || !panel.render) {
    return () => {};
  }
  designerLeftPanels.set(panel.id, panel);
  return () => {
    if (designerLeftPanels.get(panel.id) === panel) {
      designerLeftPanels.delete(panel.id);
    }
  };
}

export function getDesignerLeftPanels(): VeyopDesignerLeftPanel[] {
  return bySortedPriority(designerLeftPanels);
}

export function findDesignerLeftPanel(id: string): VeyopDesignerLeftPanel | null {
  return designerLeftPanels.get(id) ?? null;
}

export function useDesignerLeftPanels(): ComputedRef<VeyopDesignerLeftPanel[]> {
  return computed(() => bySortedPriority(designerLeftPanels));
}

export function registerDesignerLayout(layout: VeyopDesignerLayout): () => void {
  if (!layout || !layout.id || !layout.component) {
    return () => {};
  }
  designerLayouts.set(layout.id, layout);
  return () => {
    if (designerLayouts.get(layout.id) === layout) {
      designerLayouts.delete(layout.id);
    }
  };
}

export function getDesignerLayouts(): VeyopDesignerLayout[] {
  return bySortedPriority(designerLayouts);
}

export function findDesignerLayout(themeSlug?: string): VeyopDesignerLayout | null {
  const slug = themeSlug || '';
  const layouts = getDesignerLayouts();
  return layouts.find((layout) => layout.theme === slug) ?? layouts.find((layout) => !layout.theme) ?? null;
}

export function useDesignerLayouts(): ComputedRef<VeyopDesignerLayout[]> {
  return computed(() => bySortedPriority(designerLayouts));
}

export function registerUploadProcessor(processor: VeyopUploadProcessor): () => void {
  if (!processor?.id || !Array.isArray(processor.accept) || typeof processor.process !== 'function') {
    return () => {};
  }
  uploadProcessors.set(processor.id, processor);
  return () => {
    if (uploadProcessors.get(processor.id) === processor) {
      uploadProcessors.delete(processor.id);
    }
  };
}

export function getUploadProcessors(): VeyopUploadProcessor[] {
  return bySortedPriority(uploadProcessors);
}

export function useUploadProcessors(): ComputedRef<VeyopUploadProcessor[]> {
  return computed(() => bySortedPriority(uploadProcessors));
}

/** A file stored through the shopper upload, described the way image layers use it. */
export interface VeyopUploadedImage {
  /** Canvas source (medium size for photos, the original for SVG). */
  url: string;
  /** Full-size original, used for print output. */
  large: string;
  thumb: string;
  /** Value for the layer's `file` field. */
  file: string;
  /** Stored file name. */
  name: string;
}

export type VeyopImageUploader = (file: File) => Promise<VeyopUploadedImage>;

let imageUploader: VeyopImageUploader | null = null;

/**
 * The designer app installs its shopper upload here, so addons store files through the same
 * endpoint (guest-safe on every host) instead of the admin-only media API.
 */
export function setImageUploader(uploader: VeyopImageUploader | null): void {
  imageUploader = uploader;
}

export function uploadImage(file: File): Promise<VeyopUploadedImage> {
  if (!imageUploader) return Promise.reject(new Error(t('Image upload is not available')));
  return imageUploader(file);
}

// ── Image pipeline extension registries (M1 stubs) ───────────

export interface VeyopImageRasterizer {
  id: string;
  priority?: number;
  matches: (item: DesignItem) => boolean;
  rasterize: (item: DesignItem, options: { width: number; height: number }) => Promise<string | null>;
}

export interface VeyopImageExporter {
  id: string;
  priority?: number;
  format: string;
  exportItem: (item: DesignItem, options: Record<string, unknown>) => Promise<string | null>;
}

export interface VeyopPreflightRule {
  id: string;
  severity: 'warning' | 'error';
  message: string;
  matches?: (item: DesignItem, context?: VeyopPreflightContext) => boolean;
  /**
   * Optional one-tap repair shown next to the issue. `apply` returns a patch for the item
   * (or null when it cannot help); `label` is shown on the button, already translated.
   */
  fix?: {
    label: string;
    apply: (item: DesignItem, context?: VeyopPreflightContext) => Record<string, unknown> | null;
  };
}

export interface VeyopPreflightContext {
  printCode: string;
  methodPackId: string;
  view: string;
  physicalWidthCm: number;
  physicalHeightCm: number;
  areaWidthPx: number;
  areaHeightPx: number;
  embroideryProfile?: unknown;
}

export interface VeyopPrintMethodPack {
  id: string;
  label: string;
  priority?: number;
  /** Optional print-method code this pack applies to. */
  printCode?: string;
  /** Old printing codes recognized when method_pack metadata is absent. */
  legacyPrintCodes?: string[];
  /** Emit mismatch issue when matching items use another active print method. */
  enforcePrintCode?: boolean;
  preflightRules?: VeyopPreflightRule[];
  matchesItem?: (item: DesignItem) => boolean;
  rasterizeForPrint?: (
    item: DesignItem,
    options: {
      width: number;
      height: number;
      scale?: number;
      purpose?: 'preview' | 'production';
      view?: string;
      activePrintCode?: string;
      activePrintMethodPack?: string;
      /** Render the unmodified layer. Print packs decorate this output. */
      renderBase?: () => Promise<string | null>;
    },
  ) => Promise<string | null>;
}

const imageRasterizers = shallowReactive(new Map<string, VeyopImageRasterizer>());
const imageExporters = shallowReactive(new Map<string, VeyopImageExporter>());
const printMethodPacks = shallowReactive(new Map<string, VeyopPrintMethodPack>());
const preflightRules = shallowReactive(new Map<string, VeyopPreflightRule>());

/** Register a print-method-independent validation rule. */
export function registerPreflightRule(rule: VeyopPreflightRule): () => void {
  if (!rule?.id) return () => {};
  preflightRules.set(rule.id, rule);
  return () => {
    if (preflightRules.get(rule.id) === rule) preflightRules.delete(rule.id);
  };
}

export function getPreflightRules(): VeyopPreflightRule[] {
  return [...preflightRules.values()];
}

export function registerImageRasterizer(entry: VeyopImageRasterizer): () => void {
  if (!entry?.id) return () => {};
  imageRasterizers.set(entry.id, entry);
  return () => {
    if (imageRasterizers.get(entry.id) === entry) imageRasterizers.delete(entry.id);
  };
}

export function getImageRasterizers(): VeyopImageRasterizer[] {
  return bySortedPriority(imageRasterizers);
}

export function registerImageExporter(entry: VeyopImageExporter): () => void {
  if (!entry?.id) return () => {};
  imageExporters.set(entry.id, entry);
  return () => {
    if (imageExporters.get(entry.id) === entry) imageExporters.delete(entry.id);
  };
}

export function getImageExporters(): VeyopImageExporter[] {
  return bySortedPriority(imageExporters);
}

// ── Toolbar / mobile-nav extension points ────────────────────

export interface VeyopMobileNavItem {
  id: string;
  label: string;
  /** Stroke path string, or explicit stroke/material icon spec. */
  icon: DesignerIconInput;
  priority?: number;
  /** Show only when an item is selected ('selected'), only on the home rail ('default'), or always. */
  context?: 'default' | 'selected' | 'always';
  visible?: () => boolean;
  onSelect: () => void;
}

export interface VeyopToolbarAction {
  id: string;
  label: string;
  /** Stroke path string, or explicit stroke/material icon spec. */
  icon?: DesignerIconInput;
  priority?: number;
  visible?: () => boolean;
  active?: () => boolean;
  onSelect: () => void;
}

const mobileNavItems = shallowReactive(new Map<string, VeyopMobileNavItem>());
const toolbarActions = shallowReactive(new Map<string, VeyopToolbarAction>());

export function registerMobileNavItem(item: VeyopMobileNavItem): () => void {
  if (!item?.id) return () => {};
  mobileNavItems.set(item.id, item);
  return () => {
    if (mobileNavItems.get(item.id) === item) mobileNavItems.delete(item.id);
  };
}

export function getMobileNavItems(): VeyopMobileNavItem[] {
  return bySortedPriority(mobileNavItems);
}

export function useMobileNavItems(): ComputedRef<VeyopMobileNavItem[]> {
  return computed(() => bySortedPriority(mobileNavItems));
}

export function registerToolbarAction(action: VeyopToolbarAction): () => void {
  if (!action?.id) return () => {};
  toolbarActions.set(action.id, action);
  return () => {
    if (toolbarActions.get(action.id) === action) toolbarActions.delete(action.id);
  };
}

export function getToolbarActions(): VeyopToolbarAction[] {
  return bySortedPriority(toolbarActions);
}

export function useToolbarActions(): ComputedRef<VeyopToolbarAction[]> {
  return computed(() => bySortedPriority(toolbarActions));
}

export function registerPrintMethodPack(pack: VeyopPrintMethodPack): () => void {
  if (!pack?.id) return () => {};
  printMethodPacks.set(pack.id, pack);
  return () => {
    if (printMethodPacks.get(pack.id) === pack) printMethodPacks.delete(pack.id);
  };
}

export function getPrintMethodPacks(): VeyopPrintMethodPack[] {
  return bySortedPriority(printMethodPacks);
}

export function resolvePrintMethodPackForItem(item: DesignItem): VeyopPrintMethodPack | null {
  for (const pack of getPrintMethodPacks()) {
    if (pack.matchesItem?.(item)) return pack;
  }
  return null;
}

// ── Item SVG exporters (M2 — curved text, etc.) ─────────────

/** Extras the host resolves for an exporter before calling its synchronous builder. */
export interface VeyopItemSvgExportContext {
  /** Inlined `@font-face` CSS for the item's font, when `needsFontFaceCss` is set. */
  fontFaceCss?: string;
}

export interface VeyopItemSvgExporter {
  id: string;
  priority?: number;
  matches: (item: DesignItem) => boolean;
  /**
   * Awaited before `exportSvg` — load fonts or other assets the synchronous builder
   * needs. Without it an exporter that lazily loads fonts returns empty markup on the
   * first (headless) call and the layer is dropped from the composite.
   */
  prepare?: (item: DesignItem) => Promise<unknown>;
  /**
   * Set when `exportSvg` emits `<text>` rather than outlines. Exported SVG is rasterized
   * as a `data:` URL image, which cannot reach the page's webfonts, so the host resolves
   * embeddable `@font-face` CSS and passes it in the export context.
   */
  needsFontFaceCss?: boolean;
  exportSvg: (item: DesignItem, ctx?: VeyopItemSvgExportContext) => string;
}

const itemSvgExporters = shallowReactive(new Map<string, VeyopItemSvgExporter>());

export function registerItemSvgExporter(entry: VeyopItemSvgExporter): () => void {
  if (!entry?.id) return () => {};
  itemSvgExporters.set(entry.id, entry);
  return () => {
    if (itemSvgExporters.get(entry.id) === entry) itemSvgExporters.delete(entry.id);
  };
}

export function getItemSvgExporters(): VeyopItemSvgExporter[] {
  return bySortedPriority(itemSvgExporters);
}

export function resolveItemSvgExporter(item: DesignItem): VeyopItemSvgExporter | null {
  for (const exporter of getItemSvgExporters()) {
    if (exporter.matches(item)) return exporter;
  }
  return null;
}

// ── Print-area shapes (rect/rounded/circle built-ins + addon shapes) ───────

export interface VeyopPrintAreaShapeParamField {
  key: string;
  label: string;
  type: 'number' | 'text';
  min?: number;
  max?: number;
  default?: number | string;
}

export interface VeyopPrintAreaShape {
  /** Stable id; re-registering with the same id replaces the existing entry. */
  id: string;
  /** Label shown in the admin shape selector. */
  label: string;
  /** Optional one-line, already-translated explanation shown under the admin shape selector. */
  description?: string;
  /** Extra editor controls this shape needs (e.g. corner radius for 'rounded'). Omit for none. */
  paramsSchema?: { fields: VeyopPrintAreaShapeParamField[] };
  /** CSS applied to the admin's DOM print-area overlay box (cheap, non-canvas preview). */
  cssStyle: (params: Record<string, unknown>) => Record<string, string>;
  /** Canvas 2D clip path, shared by the admin preview and the designer-app renderer. */
  clipCanvas: (ctx: CanvasRenderingContext2D, w: number, h: number, params: Record<string, unknown>) => void;
  /** Inner markup for an SVG <clipPath>, used by server/production export parity checks. */
  clipSvgFragment: (w: number, h: number, params: Record<string, unknown>) => string;
  /** Lower numbers sort earlier in the shape selector (default 100). */
  priority?: number;
}

const printAreaShapes = shallowReactive(new Map<string, VeyopPrintAreaShape>());

export function registerPrintAreaShape(shape: VeyopPrintAreaShape): () => void {
  if (!shape?.id) return () => {};
  printAreaShapes.set(shape.id, shape);
  return () => {
    if (printAreaShapes.get(shape.id) === shape) printAreaShapes.delete(shape.id);
  };
}

export function getPrintAreaShapes(): VeyopPrintAreaShape[] {
  return bySortedPriority(printAreaShapes);
}

export function usePrintAreaShapes(): ComputedRef<VeyopPrintAreaShape[]> {
  return computed(() => bySortedPriority(printAreaShapes));
}

export function resolvePrintAreaShape(id: string): VeyopPrintAreaShape | null {
  return printAreaShapes.get(id) ?? null;
}

// ── Product views (front/back/left/right built-ins + addon views) ──────────

export interface VeyopProductView {
  /** Stable key, `[a-z0-9_-]{1,32}`; used inside product design blobs (`images_<id>`, `params[<id>]`). */
  id: string;
  /** Default label; a per-product `view_label` still overrides it. Pass an already-translated string. */
  label: string;
  /** Optional inline SVG markup or icon name for view-switcher tabs. */
  icon?: string;
  /** Lower numbers sort earlier: front=10, back=20, left=30, right=40 (addon default 100). */
  priority?: number;
  /** True only for the built-in views; addons must not set this. */
  core?: boolean;
  /** Defaults applied when the view is first added to a product in the catalog editor. */
  defaults?: {
    /** Print area as % of the mockup canvas. */
    printArea?: { left: number; top: number; width: number; height: number };
    printSizeCm?: { width: number; height: number };
  };
}

export const CORE_VIEW_NAMES = ['front', 'back', 'left', 'right'] as const;

/**
 * View ids share a namespace with the other keys of the product design blob,
 * so these can never be used as view ids ('top'/'bottom' are legacy export
 * positions in production-export.php).
 */
export const RESERVED_VIEW_KEYS: readonly string[] = [
  'color_hex',
  'color_title',
  'color_texture',
  'price',
  'params',
  'area',
  'catalog_thumb',
  'color_tint_enabled',
  'custom_color_enabled',
  'design_templates',
  'typographyPro',
  'top',
  'bottom',
];

const VIEW_KEY_RE = /^[a-z0-9_-]{1,32}$/;

/** True for strings that are syntactically valid, non-reserved view keys. */
export function isViewKey(value: unknown): value is string {
  return typeof value === 'string' && VIEW_KEY_RE.test(value) && !RESERVED_VIEW_KEYS.includes(value);
}

const productViews = shallowReactive(new Map<string, VeyopProductView>());

export function registerProductView(view: VeyopProductView): () => void {
  if (!view?.id || !isViewKey(view.id)) return () => {};
  productViews.set(view.id, view);
  return () => {
    if (productViews.get(view.id) === view) productViews.delete(view.id);
  };
}

export function getProductViews(): VeyopProductView[] {
  return bySortedPriority(productViews);
}

export function useProductViews(): ComputedRef<VeyopProductView[]> {
  return computed(() => bySortedPriority(productViews));
}

export function resolveProductView(id: string): VeyopProductView | null {
  return productViews.get(id) ?? null;
}

// The 4 built-ins register at module scope so they exist before addon bundles run.
registerProductView({ id: 'front', label: t('Front'), priority: 10, core: true });
registerProductView({ id: 'back', label: t('Back'), priority: 20, core: true });
registerProductView({ id: 'left', label: t('Left'), priority: 30, core: true });
registerProductView({ id: 'right', label: t('Right'), priority: 40, core: true });
